import React, { useState, useEffect, ChangeEvent, FormEvent, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { startUpload, uploadSuccess, uploadFailure } from '../../store/imageSlice';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchUserData } from '../../store/UserSlice';
import apiClient from '../../api/apiClient';
import { useTranslation } from 'react-i18next';

interface PopupContentEditProps {
    closePopups: () => void;
    existingHealthConditionId: string;
}

const PopupContentEdit: React.FC<PopupContentEditProps> = ({ closePopups, existingHealthConditionId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const [title, setTitle] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [images, setImages] = useState<string | null>(null);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [healthConditionDetails, setHealthConditionDetails] = useState<any | null>(null);

    useEffect(() => {
        const fetchHealthConditionDetails = async () => {
          try {
            const response = await apiClient.get(`/api/v1/healthConditions/${existingHealthConditionId}`);
            setHealthConditionDetails(response.data.data);
            setTitle(response.data.data.name || '');
            setImages(response.data.data.icon || '');
          } catch (error) {
            console.error('Error fetching health condition details:', error);
          }
        };
      
        if (existingHealthConditionId) {
          fetchHealthConditionDetails();
        }
    }, [existingHealthConditionId]);

    const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setTitle(e.target.value);
    };

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setSelectedImage(file);
      }
      if (file && file.type.includes('image')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelectedImage(file);
        };
        reader.readAsDataURL(file);
        setImage(file);
        setError(null);
      } else {
        setSelectedImage(null);
        setImage(null);
        setError(t('dashboard.invalidImage'));
      }
    };

    const handleSubmit = async (e: FormEvent) => {
      e.preventDefault();
      dispatch(startUpload());

      try {
        const formData = new FormData();
        if (selectedImage) {
          formData.append('icon', selectedImage);
        }
        formData.append('name', title);

        const response = await apiClient.put(
          `/api/v1/healthConditions/${existingHealthConditionId}`,
          formData
        );
        
        dispatch(uploadSuccess(response.data.imageUrl));
        dispatch(fetchUserData()).then(() => {
          closePopups();
          toast.success(t("dashboard.updateSuccess"));
        });
      } catch (error: any) {        
        const msg = error.response?.data?.error?.message || error.response?.data?.message || t('common.error');
        toast.error(msg);
        dispatch(uploadFailure(msg));
      }
    };

  return (
    <div className="popup-overlay">
    <div className="relative bg-white px-2 md:px-8 w-80 md:w-[32rem] rounded-lg text-left">
      <button onClick={closePopups} className="close-btn">
        &times;
      </button>     
    <div className="max-w-md mt-2 m-auto p-4 pb-4 md:p-8 bg-white rounded ">
      <h2 className="text-xl md:text-2xl flex font-gilroy mb-6 font-bold  text-[#263238]">
        <img src='./images/round_icon.png' alt="condition" className='object-contain pr-2'/>
        {t("dashboard.updateCondition")}
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-[#607D8B] text-sm mb-2 font-gilroy" htmlFor="image">
            {t("dashboard.addIcon")}
          </label>
          <div className="flex items-center  w-full">
          <label className={`flex flex-col items-center  w-32 h-30 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50  hover:bg-gray-100 ${selectedImage instanceof File ? "hidden" : ""}`}>
              <div className="flex flex-col items-center justify-center pt-5 pb-6 " >
                <img src='./images/gallery-add.png' alt="upload"  />
                <p className="text-xs text-[#4C535F] mt-4 font-gilroy">{t("dashboard.uploadIcon")}</p>
              </div>
              <input
                    id="image"
                    type="file"
                    name="image"
                    onChange={handleImageChange}
                    className={"visually-hidden"} // Corrected syntax
                    accept="image/*"
                    required={!images}                     
                    ref={fileInputRef}
                  />  

            </label>
            {selectedImage instanceof File && (
              <img src={URL.createObjectURL(selectedImage)} alt="preview" className="w-auto max-w-md pl-2 h-32 object-contain" />
            )}
            {images && (
            <img src={images} alt="preview" className={`w-auto max-w-md pl-2 h-32 object-contain ${selectedImage &&("hidden")} `} />
            )}

            {error && <p style={{ color: 'red' }}>{error}</p>}
          </div>
        </div>

        <div className="mb-6 mt-4">
          <label className="block text-[#607D8B] text-sm mb-2  font-gilroy" htmlFor="title">
            {t("dashboard.healthConditions")}
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={title}
            onChange={handleTitleChange}
            className="w-full  font-gilroy px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 bg-[#F5F7F8]"
            placeholder={t("dashboard.enterTitle")}
            required
          />
        </div>  
        <button
          type="submit"
          className="bg-[#B4804A] font-gilroy w-full text-white py-2 px-4 font-semibold rounded-md"
        >
          {t("dashboard.updateCondition")}
        </button>
      </form>
    </div>
    </div>
      </div>
  );
};

export default PopupContentEdit;
